import { inject } from "@angular/core";
import { CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { map, of } from "rxjs";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { StoreService } from "src/app/services/apps/store/store.service";
import { getOnboardingStage } from "./first-time-user-experience.helper";

export const preventInitialSetupAccessGuard: CanActivateFn = (
  route,
  state: RouterStateSnapshot,
) => {
  const profileService = inject(ProfileService);
  const storeService = inject(StoreService);
  const router = inject(Router);
  const userProfile = profileService.userProfile();

  // Onboarding complete — redirect to dashboard
  if (!isUserOnboardingInProgress(userProfile)) {
    return of(router.parseUrl("/dashboard"));
  }

  // Welcome already seen — redirect away from welcome route
  if (
    userProfile?.onboardingConfig?.hasSeenWelcomeScreen &&
    state.url.includes("/initial-setup/welcome")
  ) {
    return of(router.parseUrl("/dashboard"));
  }

  // Candidate-claims step: welcome not seen yet -> welcome; otherwise needs
  // at least one store that finished configuration (delivery/threshold
  // form) — that's the same moment the backend's potential-cases job was
  // already started for it.
  if (state.url.includes("/initial-setup/candidate-claims-found")) {
    if (!userProfile?.onboardingConfig?.hasSeenWelcomeScreen) {
      return of(router.parseUrl("/initial-setup/welcome"));
    }
    return storeService.getCachedOrLoad().pipe(
      map((stores) =>
        // Shared stores were analyzed when their owner linked them.
        getOnboardingStage(userProfile, stores) === "candidate-claims"
          ? true
          : router.parseUrl("/dashboard"),
      ),
    );
  }

  // Billing step: welcome not seen yet -> welcome; no store has finished
  // configuration yet -> dashboard.
  if (state.url.includes("/initial-setup/billing")) {
    if (!userProfile?.onboardingConfig?.hasSeenWelcomeScreen) {
      return of(router.parseUrl("/initial-setup/welcome"));
    }
    return storeService.getCachedOrLoad().pipe(
      map((stores) =>
        // Stores shared with the user are paid by their owner.
        getOnboardingStage(userProfile, stores) === "candidate-claims"
          ? true
          : router.parseUrl("/dashboard"),
      ),
    );
  }

  // Onboarding incomplete — allow initial-setup access
  return of(true);
};
